/**
 * Per-app “why was this blocked?” trace. **Rule order comes only from `runAppLockboxEvaluation`**
 * (`evaluate.js`); this file maps that walk to `DecisionTrace` + plan reconciliation.
 */

import { runAppLockboxEvaluation } from "./evaluate.js";

/** @typedef {import('./types.js').LockboxRulepack} LockboxRulepack */
/** @typedef {import('./types.js').EvaluationWorld} EvaluationWorld */
/** @typedef {import('./types.js').LockboxAppEntity} LockboxAppEntity */
/** @typedef {import('./types.js').EnforcementPlan} EnforcementPlan */
/** @typedef {import('./types.js').EnforcementPlan['matchedRules'][number]} MatchedRuleRow */

/**
 * @typedef {Object} RuleHit
 * @property {string} ruleId
 * @property {string} ruleName
 * @property {'mode'|'category'|'time'|'manual'} type
 */

/**
 * @typedef {Object} EvaluationStep
 * @property {number} step
 * @property {string} description
 * @property {'pass'|'block'|'allow'|'continue'} result
 */

/**
 * @typedef {Object} ConflictStep
 * @property {string} conflict
 * @property {string} winner
 * @property {string} reason
 */

/**
 * @typedef {Object} DecisionTrace
 * @property {string} app display label
 * @property {string} appId lockbox inventory id
 * @property {'block'|'allow'} finalDecision derived to match `EnforcementPlan` lists
 * @property {RuleHit[]} triggeredRules
 * @property {EvaluationStep[]} evaluationSteps
 * @property {ConflictStep[]} [conflictResolution]
 */

/**
 * @param {MatchedRuleRow} row
 * @returns {RuleHit}
 */
function matchedRuleToRuleHit(row) {
  const id = row.ruleId;
  if (id === "override:force_allow") {
    return { ruleId: id, ruleName: "Force allow", type: "manual" };
  }
  if (id === "override:force_block") {
    return { ruleId: id, ruleName: "Force block", type: "manual" };
  }
  if (id === "emergency:pause") {
    return { ruleId: id, ruleName: "Emergency pause", type: "mode" };
  }
  if (id === "emergency:allow_most") {
    return { ruleId: id, ruleName: "Emergency allow-most", type: "mode" };
  }
  if (id === "template:always_allow_category") {
    const cat = row.reason?.replace(/^Category always allowed:\s*/i, "") || row.reason;
    return { ruleId: id, ruleName: `Always allow: ${cat}`, type: "category" };
  }
  if (id === "template:always_block_category") {
    const cat = row.reason?.replace(/^Category always blocked:\s*/i, "") || row.reason;
    return { ruleId: id, ruleName: `Always block: ${cat}`, type: "category" };
  }
  if (id.startsWith("mode_strict:")) {
    return { ruleId: id, ruleName: "Stricter mode", type: "mode" };
  }
  if (id.startsWith("time_window:")) {
    return { ruleId: id, ruleName: row.reason || id, type: "time" };
  }
  return { ruleId: id, ruleName: row.reason || id, type: "category" };
}

/**
 * @param {LockboxAppEntity} app
 * @param {LockboxRulepack} pack
 * @param {EvaluationWorld} world
 * @param {EnforcementPlan} plan evaluated plan (for final block/allow sets)
 * @returns {DecisionTrace}
 */
export function buildDecisionTrace(app, pack, world, plan) {
  const { decision, matchedRule, steps } = runAppLockboxEvaluation(app, pack, world);

  const evaluationSteps = steps.map((s, i) => ({
    step: i + 1,
    description: s.description,
    result: s.result,
  }));

  /** @type {RuleHit[]} */
  const triggeredRules = [];
  if (matchedRule) triggeredRules.push(matchedRuleToRuleHit(matchedRule));
  if (decision === "neutral") {
    triggeredRules.push({
      ruleId: "default:allow",
      ruleName: "Default allow",
      type: "category",
    });
  }

  /** @type {ConflictStep[]} */
  const conflictResolution = [];
  const traced = decision === "neutral" ? "allow" : decision;
  return finalizeTrace(app, plan, traced, triggeredRules, evaluationSteps, conflictResolution);
}

/**
 * @param {LockboxAppEntity} app
 * @param {EnforcementPlan} plan
 * @param {'block'|'allow'} traced
 * @param {RuleHit[]} triggeredRules
 * @param {EvaluationStep[]} evaluationSteps
 * @param {ConflictStep[]} conflictResolution
 */
function finalizeTrace(app, plan, traced, triggeredRules, evaluationSteps, conflictResolution) {
  const fromPlan = plan.blockAppIds.includes(app.id) ? "block" : "allow";
  if (fromPlan !== traced) {
    conflictResolution.push({
      conflict: `Trace outcome (${traced}) vs plan lists (${fromPlan})`,
      winner: fromPlan,
      reason: "EnforcementPlan is authoritative; trace logic may need sync after rule changes.",
    });
  }
  return {
    app: app.displayName,
    appId: app.id,
    finalDecision: fromPlan,
    triggeredRules,
    evaluationSteps,
    conflictResolution: conflictResolution.length ? conflictResolution : undefined,
  };
}
